import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    MsalBroadcastService,
    MsalGuardConfiguration,
    MsalService,
    MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import { User } from '../../shared/models';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, Observable, Subject, filter, takeUntil } from 'rxjs';

import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import { MapStateService } from '@modules/map/map-state.service';
import { isEmptyError } from './service.helpers';
import { LoggingService } from '../../shared/services/logging.service';

type FunctionName = 'OMS Map debug' | 'FeatureFlag Access' | 'HomeView';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    private readonly _msalGuardConfig =
        inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private readonly _router = inject(Router);
    private readonly _msal = inject(MsalService);
    private readonly _msalBroadcastService = inject(MsalBroadcastService);
    private readonly _http = inject(HttpClient);
    private readonly _mapStateService = inject(MapStateService);
    private readonly _loggingService = inject(LoggingService);

    private readonly _authenticationContext$: BehaviorSubject<User>;
    private readonly _destroying$ = new Subject<void>();

    constructor() {
        this._authenticationContext$ = new BehaviorSubject(undefined);
        this._destroying$ = new Subject<void>();
        this._msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None,
                ),
                takeUntil(this._destroying$),
            )
            .subscribe(() => {
                this._refreshAuthUser();
            });
    }

    get authenticationContext(): Observable<User> {
        return this._authenticationContext$.asObservable();
    }

    get user(): User {
        return this._authenticationContext$.value;
    }

    public userHasAccessToSignOn() {
        if (!this.user) return false;

        const developerWhiteListEmails = [
            'roger.uhlin@nobina.com',
            'fredrik.falkesand@nobina.com',
            'karolina.willner@nobina.com',
            'mikael.m.pettersson@nobina.se',
            'elisabet.bergkarls@nobina.com',
            'fredrik.palmer@nobina.com',
        ];

        return _.indexOf(developerWhiteListEmails, this.user.email) >= 0;
    }

    public hasAccessToOmsFunction(functionName: FunctionName): boolean {
        if (!this.user) return false;

        // TODO: This should probably be checked by functionId and not functionName in the future.
        //       But since we haven't created the functions in the OMS db yet we do not have an id.
        //       Change this when we start working with OMS Permission properly.

        const developerWhiteListEmails = [
            'jonas.alexandersson@nobina.com',
            'johan.govers@nobina.com',
            'roger.uhlin@nobina.com',
            'bjorn.gotestrand@nobina.com',
            'linnea.mellblom@nobina.com',
            'johan.sand@nobina.com',
            'lovisa.edholm@nobina.com',
            'sven-erik.karlsson@nobina.com',
            'isak.engstrom@nobina.com',
            'fredrik.falkesand@nobina.com',
            'karolina.willner@nobina.com',
            'mia.svensson@nobina.com',
            'felix.goding@nobina.com',
            'simon.brathwaite@nobina.com',
            'mikael.m.pettersson@nobina.se',
            'fredrik.palmer@nobina.com',
            'ferdous.alam@nobina.com',
            'elisabet.bergkarls@nobina.com',
            'ahmed.abdessadki@nobina.com',
            'ingela.strom@nobina.com',
            'markus.rado@nobina.com',
            'lina.lindholm@nobina.com',
            'eva.eriksson@nobina.com',
            'christopher.asko@nobina.com',
            'guzman.bossi@nobina.com',
        ];

        const featureWhiteListEmails: string[] = [
            'fredrik.falkesand@nobina.com',
            'victoria.hendered@nobina.com',
            'fredrik.palmer@nobina.com',
            'mikael.m.pettersson@nobina.se',
            'ingela.strom@nobina.com',
            'eva.eriksson@nobina.com',
            'guzman.bossi@nobina.com',
            'ferdous.alam@nobina.com',
            'mia.svensson@nobina.com',
            'karolina.willner@nobina.com',
            'roger.uhlin@nobina.com',
            'johan.govers@nobina.com',
            'lina.lindholm@nobina.com',
            'nenad.nikolic@nobina.se',
            'elbasan.kurti@nobina.se',
            'pardeep.kumar@nobina.se',
            'mirel.tenic@nobina.se',
            'erling.andreasson@nobina.se',
            'jarno.korpi-kokko@nobina.fi',
            'lorent.shala@nobina.se',
            'torepetter.larssen@nobina.no',
        ];

        const tempWhiteListEmails: string[] = [
            'fredrik.falkesand@nobina.com',
            'victoria.hendered@nobina.com',
            'fredrik.palmer@nobina.com',
            'mikael.m.pettersson@nobina.se',
            'ingela.strom@nobina.com',
            'eva.eriksson@nobina.com',
            'guzman.bossi@nobina.com',
            'ferdous.alam@nobina.com',
            'karolina.willner@nobina.com',
            'roger.uhlin@nobina.com',
        ];

        switch (functionName) {
            case 'OMS Map debug':
                return (
                    _.indexOf(developerWhiteListEmails, this.user.email) >= 0
                );
            case 'FeatureFlag Access':
                // line view
                // set lineview on for everybody. We just comment it out for now in case anybody regrets this.
                // happened: 2026-06-02. After some weeks we can remove the feature toggle altogether
                return true;
                // return _.indexOf(featureWhiteListEmails, this.user.email) >= 0;
            case 'HomeView':
                // set homeview on for everybode. We just comment it out for now in case anybody regrets this.
                // happened: 2026-04-27. After some weeks we can remove the feature toggle altogether
                return true;
            //_.indexOf(homeViewWhiteListEmails, this.user.email) >= 0;
            default:
                console.error(
                    'Unknown OMS Function supplied. This is an implementation error.',
                );
                return false;
        }
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    // Will redirect to the loginpage of msal
    public login(force: boolean = false): void {
        if (force || !this.isOnline()) {
            if (this._msalGuardConfig.authRequest) {
                this._msal
                    .loginRedirect({
                        ...this._msalGuardConfig.authRequest,
                    } as RedirectRequest)
                    .subscribe(() => {});
            } else {
                this._msal.loginRedirect();
            }
        }
    }

    public goToLoginPage() {
        this._router.navigateByUrl('/login');
    }

    public isOnline(): boolean {
        const activeAccount = this._msal.instance.getActiveAccount();
        return !!activeAccount;
    }

    public async logout(): Promise<void> {
        await this._mapStateService.storePersistedState();
        this._msal.logoutRedirect();
    }

    private _refreshAuthUser() {
        console.log('_refreshAuthUser');
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this._msal.instance.getActiveAccount();
        if (!activeAccount && this._msal.instance.getAllAccounts().length > 0) {
            const accounts = this._msal.instance.getAllAccounts();
            activeAccount = accounts[0];
            this._msal.instance.setActiveAccount(activeAccount);
        }
        if (activeAccount) {
            this._http
                .get(
                    'https://graph.microsoft.com/v1.0/me?$select=onPremisesSamAccountName',
                )
                .subscribe({
                    next: (response: any) => {
                        const samAccountName =
                            response.onPremisesSamAccountName ?? '';
                        console.log(samAccountName);

                        const name = activeAccount.name ?? '';
                        const email = activeAccount.username ?? '';
                        const user = new User(samAccountName, name, '', email);
                        this._authenticationContext$.next(user);
                    },
                    error: (error) => {
                        // EmptyError occurs when the MsalInterceptor's inProgress$ observable
                        // completes during a login redirect (e.g. token expiry). The page is
                        // about to reload, so no action is needed.
                        if (isEmptyError(error)) {
                            return;
                        }
                        this._loggingService.logException(error);
                        console.warn('AuthService: Failed to fetch user profile from Graph API:', error);
                    }
                });
        } else {
            this._authenticationContext$.next(undefined);
        }
    }
}
